import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, inject, LOCALE_ID, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideRouter } from '@angular/router';
import { provideAuth, withAppInitializerAuthCheck } from 'angular-auth-oidc-client';
import { provideTranslateService, TranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { withNgxsReduxDevtoolsPlugin } from '@ngxs/devtools-plugin';
import { provideStore } from '@ngxs/store';
import { SI_LOCALE_CONFIG, SiLocaleId, SiLocaleService } from '@siemens/element-ng/localization';
import { provideSiFormFieldConfig } from '@siemens/element-ng/form';
import {
    provideMissingTranslationHandlerForElement,
    provideNgxTranslateForElement
} from '@siemens/element-translate-ng/ngx-translate';
import { Observable, of } from 'rxjs';
import { environment } from '@env/environment';
import { routes } from './app.routes';
import { apiIdTokenInterceptor } from '@core/auth/interceptors/api-id-token.interceptor';
import { UnauthorizedInterceptor } from '@core/auth/interceptors/unauthorized.interceptor';
import { DEFAULT_LOCALE, localeConfig } from '@core/config/locale.config';
import { GlobalSearchState } from '@core/state/global-search/global-search.state';
import { NuggetInteractionState } from '@core/state/nugget-interaction/nugget-interaction.state';
import { UserContextState } from '@core/state/user-context/user-context.state';

function initializeLanguage(
    translateService: TranslateService,
    localeService: SiLocaleService
): () => Observable<undefined> {
    return () => {
        const locale = localeService.locale;
        translateService.setFallbackLang(DEFAULT_LOCALE);
        translateService.use(locale);
        return of(undefined);
    };
}

export const appConfig: ApplicationConfig = {
    providers: [
        importProvidersFrom(BrowserModule),
        provideRouter(routes),

        // Locale configuration for Element framework
        { provide: LOCALE_ID, useClass: SiLocaleId, deps: [SiLocaleService] },
        { provide: SI_LOCALE_CONFIG, useValue: localeConfig },

        // Translation configuration with ngx-translate
        provideTranslateService({
            loader: provideTranslateHttpLoader({
                prefix: '/assets/i18n/',
                suffix: `.json?hash=${environment.hash}`
            }),
            fallbackLang: DEFAULT_LOCALE,
            missingTranslationHandler: provideMissingTranslationHandlerForElement()
        }),

        // Enable ngx-translate for Element components
        provideNgxTranslateForElement(),
        provideSiFormFieldConfig(),

        provideHttpClient(withInterceptors([apiIdTokenInterceptor]), withInterceptorsFromDi()),
        provideAuth({ config: environment.authentication }, withAppInitializerAuthCheck()),
        provideStore(
            [UserContextState, NuggetInteractionState, GlobalSearchState],
            { developmentMode: !environment.production, compatibility: { strictContentSecurityPolicy: true } },
            withNgxsReduxDevtoolsPlugin()
        ),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: UnauthorizedInterceptor,
            multi: true
        },
        provideAppInitializer(() => {
            const initializerFn = initializeLanguage(inject(TranslateService), inject(SiLocaleService));
            return initializerFn();
        })
    ]
};
